import { useState, useEffect } from 'react';
import { HiArrowRight, HiCheckCircle, HiXCircle, HiRefresh, HiStar, HiLightBulb } from 'react-icons/hi';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const LETTRES = ['A', 'B', 'C', 'D', 'E'];

const QuizView = ({ coursId, onComplete }) => {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const res = await api.get(`/formations/cours/${coursId}/quiz`);
        if (res) setQuiz(res);
      } catch (err) {
        toast.error("Erreur lors du chargement du quiz");
      } finally {
        setLoading(false);
      }
    };
    fetchQuiz();
  }, [coursId]);

  const recommencer = () => {
    setCurrentIndex(0);
    setSelectedOption(null);
    setShowFeedback(false);
    setScore(0);
    setIsFinished(false);
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Chargement du quiz...</div>;

  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return (
      <div className="p-10 text-center text-gray-500 border border-gray-200 rounded-lg">
        Aucune question n'a été configurée pour ce quiz.
      </div>
    );
  }

  const currentQuestion = quiz.questions[currentIndex];
  const bonneReponse = selectedOption?.is_correct;

  const handleValidation = () => {
    if (!selectedOption) return toast.error("Choisis une réponse !");
    if (selectedOption.is_correct) {
      setScore(prev => prev + 1);
      toast.success("Bonne réponse !", { icon: '🎉' });
    } else {
      toast("Pas tout à fait...", { icon: '🤔' });
    }
    setShowFeedback(true);
  };

  const nextQuestion = () => {
    setShowFeedback(false);
    setSelectedOption(null);
    if (currentIndex < quiz.questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setIsFinished(true);
      const finalScorePercent = Math.round((score / quiz.questions.length) * 100);
      onComplete(finalScorePercent >= quiz.score_min);
    }
  };

  if (isFinished) {
    const finalScorePercent = Math.round((score / quiz.questions.length) * 100);
    const success = finalScorePercent >= quiz.score_min;
    return (
      <div className="text-center p-10 space-y-6 border border-gray-200 rounded-2xl bg-gradient-to-b from-white to-gray-50 shadow-sm max-w-2xl mx-auto">
        <div className="text-6xl animate-bounce">{success ? '🏆' : '💪'}</div>
        <div>
          <h2 className="text-3xl font-extrabold text-gray-800">{success ? "Bravo, quiz validé !" : "Presque !"}</h2>
          <p className="text-gray-600 mt-3 text-lg">
            Ton score : <span className={`font-black text-2xl ${success ? 'text-green-600' : 'text-orange-500'}`}>{finalScorePercent}%</span>
            <span className="text-gray-400 text-sm ml-2">({score}/{quiz.questions.length} bonnes réponses)</span>
          </p>
          <div className="mt-4 h-4 bg-gray-200 rounded-full overflow-hidden max-w-sm mx-auto relative">
            <div className={`h-full transition-all duration-1000 ${success ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-orange-400 to-red-400'}`} style={{ width: `${finalScorePercent}%` }} />
            <div className="absolute top-0 bottom-0 border-r-2 border-dashed border-gray-500" style={{ left: `${quiz.score_min}%` }}></div>
          </div>
          <p className="text-xs text-gray-400 mt-2">Score minimum pour valider : {quiz.score_min}%</p>
        </div>
        {success ? (
          <p className="text-green-700 bg-green-50 border border-green-200 rounded-xl p-4 text-sm font-medium">
            🎓 Excellent travail ! Tu peux maintenant terminer ce chapitre et passer au suivant.
          </p>
        ) : (
          <button onClick={recommencer} className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-8 py-3 rounded-xl font-bold hover:scale-[1.03] active:scale-[0.98] transition-all shadow-lg shadow-blue-500/20">
            <HiRefresh className="text-xl" /> Réessayer le quiz
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <span className="text-xs font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full whitespace-nowrap">
          Question {currentIndex + 1} / {quiz.questions.length}
        </span>
        <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500" style={{ width: `${((currentIndex + (showFeedback ? 1 : 0)) / quiz.questions.length) * 100}%` }} />
        </div>
        <span className="flex items-center gap-1 text-sm font-bold text-amber-500 whitespace-nowrap"><HiStar className="text-lg" /> {score}</span>
      </div>

      <h2 className="text-xl font-bold text-gray-800 leading-snug">{currentQuestion.texte_question}</h2>

      <div className="space-y-3">
        {currentQuestion.options.map((option, idx) => {
          const estSelectionnee = selectedOption?.id === option.id;
          let styleCarte = 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md hover:scale-[1.01]';
          let styleLettre = 'bg-gray-100 text-gray-600';
          if (!showFeedback && estSelectionnee) { styleCarte = 'border-blue-500 bg-blue-50 shadow-md'; styleLettre = 'bg-blue-500 text-white'; }
          if (showFeedback && option.is_correct) { styleCarte = 'border-green-500 bg-green-50 shadow-md'; styleLettre = 'bg-green-500 text-white'; }
          if (showFeedback && estSelectionnee && !option.is_correct) { styleCarte = 'border-red-400 bg-red-50'; styleLettre = 'bg-red-400 text-white'; }
          return (
            <button key={option.id} disabled={showFeedback} onClick={() => setSelectedOption(option)}
              className={`w-full p-4 text-left border-2 rounded-xl transition-all duration-200 flex items-center gap-4 ${styleCarte} ${showFeedback ? 'cursor-default' : 'cursor-pointer'}`}>
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-sm flex-shrink-0 transition-colors ${styleLettre}`}>{LETTRES[idx] || '?'}</span>
              <span className="text-sm font-medium text-gray-700 flex-1">{option.texte_option}</span>
              {showFeedback && option.is_correct && <HiCheckCircle className="text-green-500 text-2xl flex-shrink-0" />}
              {showFeedback && estSelectionnee && !option.is_correct && <HiXCircle className="text-red-400 text-2xl flex-shrink-0" />}
            </button>
          );
        })}
      </div>

      {showFeedback && currentQuestion.explication && (
        <div className={`p-4 rounded-xl text-sm flex gap-3 items-start border ${bonneReponse ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
          <HiLightBulb className="text-xl flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-bold block mb-1">{bonneReponse ? 'Exactement !' : 'À retenir :'}</span>
            {currentQuestion.explication}
          </div>
        </div>
      )}

      {!showFeedback ? (
        <button onClick={handleValidation} className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-3.5 rounded-xl font-bold hover:scale-[1.01] active:scale-[0.99] transition-all shadow-lg shadow-blue-500/20">
          Vérifier ma réponse
        </button>
      ) : (
        <button onClick={nextQuestion} className="w-full bg-gray-800 text-white py-3.5 rounded-xl font-bold hover:bg-gray-700 transition-colors inline-flex items-center justify-center gap-2">
          {currentIndex < quiz.questions.length - 1 ? "Question suivante" : "Voir mon résultat"}
          <HiArrowRight />
        </button>
      )}
    </div>
  );
};

export default QuizView;
